import { IAccountRespSchema, IQuoteRespSchema } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  previewQuoteTerms,
  rulesV3ServiceQueryKeys,
} from '../api/ruleServiceV3';
import { useDebounce } from './useDebounce';

/**
 * Resolve the {{...}} template expressions in one or more rich-text terms
 * against a quote (and optional account) via /rules-v3/preview-terms.
 * Term changes are debounced so previews can update live while typing.
 * `resolvedTerms` maps 1:1 (same order) to the `terms` input.
 */
export function useTermsPreview({
  quote,
  account,
  terms,
  enabled,
  isAccountLoading = false,
  debounceMs = 400,
  wrapListItems,
}: {
  quote: Maybe<IQuoteRespSchema>;
  account?: Maybe<IAccountRespSchema>;
  terms: string[];
  enabled: boolean;
  /**
   * Whether the caller's account query is still in flight. Resolving before
   * it lands renders every {{account.*}} expression blank and then silently
   * swaps it for the real value — a wrong preview shown as if it were right.
   */
  isAccountLoading?: boolean;
  debounceMs?: number;
  /**
   * Whether list-item text is span-wrapped, matching how the PDF renders this
   * term type: true (default) for conditional terms, false for contract terms.
   */
  wrapListItems?: boolean;
}): {
  resolvedTerms: string[] | null;
  isLoading: boolean;
  error: unknown;
} {
  // Serialize so callers with unstable array identities don't retrigger the
  // debounce or the query key on every render
  const serializedTerms = useMemo(() => JSON.stringify(terms), [terms]);
  const debouncedTerms = useDebounce(serializedTerms, debounceMs);
  // The request must be built from the debounced value, not the live one, or
  // the first keystroke fires a request whose body is still the old terms
  const debouncedTermsArray = useMemo(
    () => JSON.parse(debouncedTerms) as string[],
    [debouncedTerms],
  );

  const { data, isFetching, error } = useQuery({
    queryKey: rulesV3ServiceQueryKeys.previewTerms({
      quoteId: quote?.id,
      quoteModifyDate: quote?.modifyDate,
      accountId: account?.id ?? null,
      wrapListItems: wrapListItems ?? true,
      serializedTerms: debouncedTerms,
    }),
    queryFn: () =>
      previewQuoteTerms({
        record: {
          quote: quote as unknown as Record<string, unknown>,
          account: account
            ? (account as unknown as Record<string, unknown>)
            : undefined,
        },
        terms: debouncedTermsArray,
        wrapListItems,
      }),
    enabled:
      enabled && !!quote && !isAccountLoading && debouncedTermsArray.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });

  // placeholderData keeps serving the previous query's result while a new one
  // is in flight. Callers index the result positionally, so handing back an
  // array built from a different number of terms would render one term's
  // resolved text under another term's heading — withhold it until it fits.
  const resolvedTerms =
    data?.resolvedTerms.length === terms.length ? data.resolvedTerms : null;

  return {
    resolvedTerms,
    isLoading: isFetching,
    error,
  };
}
